import { getApolloContext, useSuspenseQuery } from '@apollo/client'
import { FetchMyIdentityDocument } from '@salescore/client-api'
import { Loading } from '@salescore/client-common'
import { SelectUser } from '@salescore/client-feature-setting'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { Button } from 'antd'
import { type ReactNode, useContext, useEffect } from 'react'

import { AuthenticatedApolloProvider } from '../providers/AuthenticatedApolloProvider'

const UserNotFound = (): ReactNode => (
  <div className="flex size-full flex-col items-center justify-center text-center align-middle text-gray-400">
    <div>
      このアカウントが所属する組織はありません。
      <br />
      組織設定がまだの方は、弊社営業担当者までお問い合わせください。
      <br />
      既に組織に所属している場合は、違うアカウントで再度ログインを試してください。
    </div>
    <div className="mt-4">
      <Link to="/sign_out">
        <Button type="primary">ログインページへ</Button>
      </Link>
    </div>
  </div>
)

const Redirecting = ({ redirect }: { redirect: () => void }): ReactNode => {
  useEffect(() => {
    redirect()
  }, [])

  return <Loading message={`リダイレクト中...`} />
}

const Body = (): ReactNode => {
  const navigate = useNavigate()
  const {
    data: { myIdentity },
  } = useSuspenseQuery(FetchMyIdentityDocument)
  const { client } = useContext(getApolloContext())

  useEffect(() => {
    // 古いキャッシュを読み込み続けてエラーになるケースがあるため、キャッシュクリアする
    void client?.resetStore()
  }, [])

  // 新規登録時、Auth0からリダイレクトされた段階ではAuth0アカウントがあり、Identityがない状態
  if (myIdentity === undefined || myIdentity === null) {
    return (
      <Redirecting
        redirect={() => {
          void navigate({ to: '/sign_up' })
        }}
      />
    )
  }

  if (myIdentity.users.length > 1) {
    return <SelectUser myIdentity={myIdentity} />
  }
  const [user] = myIdentity.users

  if (user?.organization.id !== undefined) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const { organization } = myIdentity.users[0]!
    const organizationId = organization.id
    return (
      <Redirecting
        redirect={() => {
          void navigate({ to: '/o/$organizationId/v2', params: { organizationId } })
        }}
      />
    )
  }
  return <UserNotFound />
}

const Page = (): ReactNode => (
  <AuthenticatedApolloProvider>
    <Body />
  </AuthenticatedApolloProvider>
)

export const Route = createFileRoute('/')({
  component: Page,
})
